import { ReactElement, RefObject, useEffect, useId, useRef } from 'react';
import { ComponentRendering } from '@sitecore-jss/sitecore-jss/layout';
import { Field } from '@sitecore-jss/sitecore-jss-nextjs';

import { useCondensedMenu } from 'common/condensedMenu/CondensedMenuProvider';

type CondensedMenuFields = {
  condensedMenuAnchor?: Field<string>;
  condensedMenuEnabled?: Field<boolean>;
  condensedMenuTitle?: Field<string>;
};

export type CondensedMenuItem = CondensedMenuFields & {
  data?: { item?: CondensedMenuFields };
};

export interface CondensedMenuWrapperProps {
  children: ReactElement;
  rendering: ComponentRendering<CondensedMenuItem>;
}

export const CondensedMenuWrapper = ({ children, rendering }: CondensedMenuWrapperProps) => {
  const id = useId();

  const parsedProps = rendering.fields;

  const paddingProps = rendering.fields?.data ? rendering.fields.data.item : parsedProps;
  const name = paddingProps?.condensedMenuTitle?.value ?? rendering.componentName;
  const anchor = paddingProps?.condensedMenuAnchor?.value ?? rendering.uid ?? id;
  const enabled = !!paddingProps?.condensedMenuEnabled?.value;
  const ref = useCondensedMenuAnchor(name, anchor, enabled);

  if (!enabled) {
    return <>{children}</>;
  }

  return (
    <div ref={ref} id={anchor} tabIndex={-1}>
      {children}
    </div>
  );
};

export const useCondensedMenuAnchor = (
  name: string | undefined,
  anchor: string | undefined,
  enabled: boolean
) => {
  const { registerItem, unregisterItem } = useCondensedMenu();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (enabled && anchor) {
      registerItem({ name, anchor, anchorElement: ref as RefObject<HTMLElement> });
    }
    return () => {
      if (anchor) {
        unregisterItem(anchor);
      }
    };
  }, [registerItem, unregisterItem, name, anchor, enabled]);
  return ref;
};
